import React from 'react';
import PropTypes from 'prop-types';
import { Box, CircularProgress, Divider, IconButton, Tooltip } from '@mui/material';
import {
  AutoAwesome as AutoAwesomeIcon,
  Collections as CollectionsIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Favorite as FavoriteIcon,
  FavoriteBorder as FavoriteBorderIcon,
  FormatQuote as ExposureMentionIcon,
  Groups as ExposureSharedIcon,
  Label as LabelIcon,
  Public as PublicIcon,
  SentimentSatisfied as SentNeutralIcon,
  SentimentVeryDissatisfied as SentNegativeIcon,
  SentimentVerySatisfied as SentPositiveIcon,
  Share as ShareIcon,
  Star as ExposureDedicatedIcon,
  RemoveCircleOutline as ExposureNoneIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon
} from '@mui/icons-material';
import { operationalStatusColors } from '../../../../design-system/tokens';
import { QUALITATIVE_AI_ACCENT } from '../../../../utils/aiAnalysisStatus';
import ArticleModalReadingTools from './ArticleModalReadingTools';

const ArticleModalFooterActions = ({
  article,
  highlightEnabled,
  isPaused,
  isSearchOpen,
  isSpeaking,
  isTTSSupported,
  kpiaAnalyzing,
  qualitativeAnalysisApplied,
  onAddToCollection,
  onAnalyzeKpia,
  onDelete,
  onEdit,
  onListen,
  onOpenExposureMenu,
  onOpenPublicLink,
  onOpenSearch,
  onOpenShare,
  onOpenTagDialog,
  onOpenSentimentMenu,
  onToggleFavorite,
  onToggleHighlight,
  onToggleRead,
  readOnly
}) => readOnly ? (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, flexShrink: 0, width: 'max-content' }}>
    <ArticleModalReadingTools
      highlightEnabled={highlightEnabled}
      isPaused={isPaused}
      isSearchOpen={isSearchOpen}
      isSpeaking={isSpeaking}
      isTTSSupported={isTTSSupported}
      onListen={onListen}
      onOpenSearch={onOpenSearch}
      onToggleHighlight={onToggleHighlight}
      showHighlight={false}
    />

    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />

    <Tooltip title="Compartilhar" arrow>
      <IconButton onClick={onOpenShare} color="inherit">
        <ShareIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title="Link Publico" arrow>
      <IconButton onClick={onOpenPublicLink} color="inherit">
        <PublicIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>
  </Box>
) : (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.2, flexShrink: 0, width: 'max-content' }}>
    <ArticleModalReadingTools
      highlightEnabled={highlightEnabled}
      isPaused={isPaused}
      isSearchOpen={isSearchOpen}
      isSpeaking={isSpeaking}
      isTTSSupported={isTTSSupported}
      onListen={onListen}
      onOpenSearch={onOpenSearch}
      onToggleHighlight={onToggleHighlight}
    />

    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />

    <Tooltip title="Compartilhar" arrow>
      <IconButton onClick={onOpenShare} color="inherit">
        <ShareIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title="Link Público" arrow>
      <IconButton onClick={onOpenPublicLink} color="inherit">
        <PublicIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />

    <Tooltip title={article.isFavorite ? 'Remover dos Favoritos' : 'Adicionar aos Favoritos'} arrow>
      <IconButton onClick={onToggleFavorite} sx={{ color: article.isFavorite ? 'error.main' : 'inherit' }}>
        {article.isFavorite ? <FavoriteIcon sx={{ fontSize: 22 }} /> : <FavoriteBorderIcon sx={{ fontSize: 22 }} />}
      </IconButton>
    </Tooltip>

    <Tooltip title={article.isRead ? 'Marcar como Não Lido' : 'Marcar como Lido'} arrow>
      <IconButton onClick={onToggleRead} sx={{ color: article.isRead ? 'primary.main' : 'inherit' }}>
        {article.isRead ? <VisibilityIcon sx={{ fontSize: 22 }} /> : <VisibilityOffIcon sx={{ fontSize: 22 }} />}
      </IconButton>
    </Tooltip>

    <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />

    <Tooltip title="Adicionar Tag" arrow>
      <IconButton onClick={onOpenTagDialog} color="inherit">
        <LabelIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title="Adicionar à Coleção" arrow>
      <IconButton onClick={onAddToCollection} color="inherit">
        <CollectionsIcon sx={{ fontSize: 22 }} />
      </IconButton>
    </Tooltip>

    <Tooltip title="Sentimento" arrow>
      <IconButton onClick={onOpenSentimentMenu} color="inherit">
        {article.sentiment === 'positive' ? <SentPositiveIcon sx={{ fontSize: 22, color: '#4caf50' }} /> : article.sentiment === 'negative' ? <SentNegativeIcon sx={{ fontSize: 22, color: '#f44336' }} /> : <SentNeutralIcon sx={{ fontSize: 22, color: '#9e9e9e' }} />}
      </IconButton>
    </Tooltip>

    <Tooltip title="Exposição da Marca" arrow>
      <IconButton onClick={onOpenExposureMenu} color="inherit">
        {article.exposure === 'dedicated' ? <ExposureDedicatedIcon sx={{ fontSize: 22, color: operationalStatusColors.success }} /> : article.exposure === 'shared' ? <ExposureSharedIcon sx={{ fontSize: 22, color: operationalStatusColors.info }} /> : article.exposure === 'mention' ? <ExposureMentionIcon sx={{ fontSize: 22, color: 'grey.500' }} /> : <ExposureNoneIcon sx={{ fontSize: 22, color: 'grey.400' }} />}
      </IconButton>
    </Tooltip>

    <Tooltip title={kpiaAnalyzing ? 'Calculando Analise Qualitativa...' : qualitativeAnalysisApplied ? 'Analise por IA aplicada' : 'Aplicar Analise Qualitativa (Ocupacao e Sentimento)'} arrow>
      <IconButton
        onClick={onAnalyzeKpia}
        disabled={kpiaAnalyzing}
        sx={{ color: qualitativeAnalysisApplied ? QUALITATIVE_AI_ACCENT : 'secondary.main' }}
      >
        {kpiaAnalyzing ? <CircularProgress size={18} color="inherit" /> : <AutoAwesomeIcon sx={{ fontSize: 22 }} />}
      </IconButton>
    </Tooltip>

    {(onEdit || onDelete) && <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 1.5 }} />}

    {onEdit && (
      <Tooltip title="Editar" arrow>
        <IconButton onClick={() => onEdit(article)} color="inherit">
          <EditIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    )}

    {onDelete && (
      <Tooltip title="Excluir" arrow>
        <IconButton onClick={() => onDelete(article._id)} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ fontSize: 22 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

ArticleModalFooterActions.propTypes = {
  article: PropTypes.shape({
    _id: PropTypes.string,
    exposure: PropTypes.string,
    isFavorite: PropTypes.bool,
    isRead: PropTypes.bool,
    sentiment: PropTypes.string
  }).isRequired,
  highlightEnabled: PropTypes.bool.isRequired,
  isPaused: PropTypes.bool.isRequired,
  isSearchOpen: PropTypes.bool.isRequired,
  isSpeaking: PropTypes.bool.isRequired,
  isTTSSupported: PropTypes.bool.isRequired,
  kpiaAnalyzing: PropTypes.bool.isRequired,
  onAddToCollection: PropTypes.func.isRequired,
  onAnalyzeKpia: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onEdit: PropTypes.func,
  onListen: PropTypes.func.isRequired,
  onOpenExposureMenu: PropTypes.func.isRequired,
  onOpenPublicLink: PropTypes.func.isRequired,
  onOpenSearch: PropTypes.func.isRequired,
  onOpenSentimentMenu: PropTypes.func.isRequired,
  onOpenShare: PropTypes.func.isRequired,
  onOpenTagDialog: PropTypes.func.isRequired,
  onToggleFavorite: PropTypes.func.isRequired,
  onToggleHighlight: PropTypes.func.isRequired,
  onToggleRead: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  qualitativeAnalysisApplied: PropTypes.bool.isRequired
};

ArticleModalFooterActions.defaultProps = {
  onDelete: null,
  onEdit: null,
  readOnly: false
};

export default ArticleModalFooterActions;
